<template>
  <v-dialog
    v-model="dialog"
    max-width="900"
    scrollable
  >
    <v-form @submit.prevent="submit">
      <v-card>
        <v-toolbar
          flat
          dark
          color="primary"
        >
          {{ $t('Labels and texts') }}
          <v-spacer />
          <v-btn
            icon
            dark
            @click="dialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-0">
          <v-breadcrumbs :items="breadcrumbs" />

          <v-tabs
            v-model="activeTab"
            class="mb-6"
            background-color="transparent"
            flat
            fixed-tabs
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-arrows
          >
            <v-tab
              v-for="item in tabs"
              :key="item.value"
            >
              {{ item.title }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <v-tab-item key="general">
              <div class="elevation-2 mx-2 my-1 px-4 py-4 mb-6">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Search placeholder text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.search_placeholder"
                      :autofocus="form.search_placeholder === null"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.search_placeholder"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.search_placeholder || defaultStrings.search_placeholder"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('User feedback saved text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.user_feedback_saved"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.user_feedback_saved"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.user_feedback_saved || defaultStrings.user_feedback_saved"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Search results text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.search_results"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.search_results"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.search_results || defaultStrings.search_results"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('No search results text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.no_search_results"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.no_search_results"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.no_search_results || defaultStrings.no_search_results"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('User feedback text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.user_feedback"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.user_feedback"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.user_feedback || defaultStrings.user_feedback"
                    />
                  </v-col>
                </v-row>
              </div>

              <div class="elevation-2 mx-2 my-1 px-4 py-4 mb-6">
                <p class="text-h6 mt-0 mb-6">
                  {{ $t('Contact information') }}
                </p>

                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Link text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.contact_url_text"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.contact_url_text"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.contact_url_text"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    sm="12"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Description') }}
                      </p>
                    </div>

                    <v-textarea
                      v-model="form.contact_description"
                      :placeholder="faq.default_translation.contact_description"
                      :error-messages="errors.contact_description"
                      hide-details
                      outlined
                      rows="2"
                    />
                  </v-col>
                </v-row>
              </div>

              <div class="elevation-2 mx-2 my-1 px-4 py-4">
                <p class="text-h6 mt-0 mb-6">
                  {{ $t('Contact form') }}
                </p>

                <v-row
                  v-for="field in faq.contact_form_fields"
                  :key="`contact-form-field-${field.id}`"
                  cols="12"
                  sm="6"
                >
                  <v-col
                    cols="12"
                    sm="4"
                    class="d-flex align-center justify-start"
                  >
                    {{ field.default_translation.label }} ({{ field.type }})
                  </v-col>
                  <v-col
                    cols="12"
                    sm="4"
                  >
                    <v-text-field
                      v-model="form.contact_form_fields[field.id].label"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors[`contact_form_fields.${field.id}.label`]"
                      hide-details="auto"
                      outlined
                      dense
                      :label="$t('Label')"
                      persistent-placeholder
                      :placeholder="field.default_translation.label"
                    />
                  </v-col>
                  <v-col
                    v-if="field.type !== 'checkbox'"
                    cols="12"
                    sm="4"
                  >
                    <v-text-field
                      v-model="form.contact_form_fields[field.id].placeholder"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors[`contact_form_fields.${field.id}.placeholder`]"
                      hide-details="auto"
                      outlined
                      dense
                      :label="$t('Placeholder')"
                      persistent-placeholder
                      :placeholder="field.default_translation.placeholder"
                    />
                  </v-col>
                  <template v-if="field.type === 'select'">
                    <v-row>
                      <v-col offset-sm="4">
                        <v-expansion-panels class="mb-2 pr-2">
                          <v-expansion-panel>
                            <v-expansion-panel-header class="justify-self-start">
                              {{ $t('Field options') }}
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <v-text-field
                                v-for="(option, idx) in field.options"
                                :key="option.value"
                                v-model="form.contact_form_fields[field.id].options[option.id].text"
                                :loading="form.processing"
                                :disabled="form.processing"
                                outlined
                                dense
                                hide-details
                                :class="{
                                  'mb-6': idx < field.options.length - 1,
                                  'mb-2': idx === field.options.length - 1
                                }"
                                label="Ange val"
                                persistent-placeholder
                                :placeholder="option.default_translation.text"
                              />
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-col>
                    </v-row>
                  </template>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Heading') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.contact_form_heading"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.contact_form_heading"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.contact_form_heading"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Description') }}
                      </p>
                    </div>

                    <v-textarea
                      v-model="form.contact_form_description"
                      :rows="2"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.contact_form_description"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.contact_form_description"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Submit button text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.contact_form_submit"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.contact_form_submit"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.contact_form_submit"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="12"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Confirmation message') }}
                      </p>
                    </div>

                    <v-textarea
                      v-model="form.contact_form_success_message"
                      :rows="2"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.contact_form_success_message"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.contact_form_success_message"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <v-tab-item key="embed">
              <div class="elevation-2 mx-2 my-1 px-4 py-4 mb-4">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Show all button text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.embed_show_all"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.embed_show_all"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.embed_show_all || defaultStrings.embed_show_all"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <v-tab-item key="widget">
              <div class="elevation-2 mx-2 my-1 px-4 py-4 mb-4">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Toggle button text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.widget_label"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.widget_label"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.widget_label"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('FAQ tab title') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.widget_faq_title"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.widget_faq_title"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.widget_faq_title"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Contact form tab title') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.widget_contact_form_title"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.widget_contact_form_title"
                      hide-details="auto"
                      outlined
                      dense
                      :placeholder="faq.default_translation.widget_contact_form_title"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <v-tab-item key="portal">
              <div class="elevation-2 mx-2 my-1 px-4 py-4 mb-4">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Website link text') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.portal_website_link_text"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.portal_website_link_text"
                      hide-details="auto"
                      :placeholder="faq.default_translation.portal_website_link_text"
                      outlined
                      dense
                      persistent-hint
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Portal title') }}
                      </p>
                    </div>

                    <v-text-field
                      v-model="form.portal_title"
                      :loading="form.processing"
                      :disabled="form.processing"
                      :error-messages="errors.portal_title"
                      hide-details="auto"
                      :placeholder="faq.default_translation.portal_title"
                      outlined
                      dense
                      persistent-hint
                      :maxlength="60"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    sm="12"
                  >
                    <div class="mb-3">
                      <p class="mb-0 subtitle-2">
                        {{ $t('Subtitle') }}
                      </p>
                    </div>
                    <v-textarea
                      v-model="form.portal_subtitle"
                      :loading="form.processing"
                      :error-messages="errors.portal_subtitle"
                      :placeholder="faq.default_translation.portal_subtitle"
                      hide-details="auto"
                      outlined
                      dense
                      rows="3"
                      :maxlength="150"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="dialog = false"
          >
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn
            type="submit"
            color="primary"
            :loading="form.processing"
            :disabled="form.processing"
            text
          >
            {{ $t('Save changes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
export default {
  props: {
    faq: {
      type: Object,
      required: true
    },
    faqTranslation: {
      type: Object,
      required: false,
      default: null
    },
    defaultStrings: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({}),
    }
  },

  data() {
    return {
      dialog: true,
      activeTab: '',
      form: this.$inertia.form('UpdateFaqTranslation', {
        language_code: this.faqTranslation.language_code,
        search_placeholder: this.faqTranslation.search_placeholder,
        embed_show_all: this.faqTranslation.embed_show_all,
        search_results: this.faqTranslation.search_results,
        no_search_results: this.faqTranslation.no_search_results,
        user_feedback: this.faqTranslation.user_feedback,
        user_feedback_saved: this.faqTranslation.user_feedback_saved,
        widget_label: this.faqTranslation.widget_label,
        widget_contact_form_title: this.faqTranslation.widget_contact_form_title,
        widget_faq_title: this.faqTranslation.widget_faq_title,
        portal_website_link_text: this.faqTranslation.portal_website_link_text,
        portal_title: this.faqTranslation.portal_title,
        portal_subtitle: this.faqTranslation.portal_subtitle,
        contact_description: this.faqTranslation.contact_description,
        contact_url_text: this.faqTranslation.contact_url_text,
        contact_form_heading: this.faqTranslation.contact_form_heading,
        contact_form_description: this.faqTranslation.contact_form_description,
        contact_form_submit: this.faqTranslation.contact_form_submit,
        contact_form_success_message: this.faqTranslation.contact_form_success_message,
        contact_form_fields: this.faq.contact_form_fields.reduce((acc, field) => {
          const fieldTranslation = field.translations.find(t => t.language_code === this.faqTranslation.language_code);

          acc[field.id] = {
            id: field.id,
            label: fieldTranslation.label,
            placeholder: fieldTranslation.placeholder,
            options: field.options.reduce((acc, option) => {
              const optionTranslation = option.translations.find(o => o.language_code === this.faqTranslation.language_code);

              acc[option.id] = {
                text: optionTranslation.text,
              }

              return acc;
            }, {})
          }

          return acc;
        }, {})
      })
    }
  },

  computed: {
    breadcrumbs() {
      return [
        { text: this.faq.all_languages[this.faqTranslation.language_code].name },
        { text: this.$t('Labels and texts') },
      ];
    },

    tabs() {
      return [
        {
          title: this.$t('General'),
          value: '#general',
        },
        {
          title: this.$t('Embed on page'),
          value: '#embed',
        },
        {
          title: this.$t('Use as widget'),
          value: '#widget',
        },
        {
          title: this.$t('FAQ Portal'),
          value: '#portal',
        },
      ]
    },
  },

  watch: {
    dialog: function(value) {
      if (value === false) {
        this.form.reset();
        const backUrl = this.route('faq.translate', this.faq.id);
        this.$inertia.visit(backUrl, { preserveState: true, preserveScroll: true });
      }
    },
  },

  created() {
    this.form.reset();
  },

  methods: {
    submit() {
      let url = this.route('faq-translation.update', {
        faq: this.faq.id,
        faqTranslation: this.faqTranslation.id
      });

      this.form.patch(url, {
        preserveState: true,
        onSuccess: () => this.form.reset()
      });
    }
  }
}
</script>
